import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiSend } from '@jmvilaca-corp/auth-ui'
import { standardSource } from './connectors'

export type CalendarRole = 'owner' | 'fullControl' | 'readOnly'
export type CalendarAccess = 'readOnly' | 'fullControl'

export interface CalendarSummary {
  calendarId: string
  name: string
  color: string
  role: CalendarRole
  /** Type du calendrier : `standard`, ou la cle d'un connecteur (`sportcorico`). */
  source: string
  ownerName: string | null
  ownerEmail: string | null
}

export interface CalendarShare {
  userId: string
  email: string
  displayName: string
  access: CalendarAccess
}

/**
 * Place d'un evenement importe pour le compte lie a son calendrier : un de ses
 * joueurs est convoque, ou ne l'est pas encore mais c'est son equipe
 * habituelle (`expected`), ou rien ne l'y attend.
 */
export type EventParticipation = 'convoked' | 'expected' | 'other'

export interface CalendarEvent {
  eventId: string
  calendarId: string
  type: string
  title: string
  startsAt: string
  endsAt: string
  isAllDay: boolean
  location: string | null
  description: string | null
  details: Record<string, string>
  /** Score d'un match importe qui en a un. `isHome` : l'equipe du calendrier recoit. */
  score: { home: number; outside: number; isHome: boolean | null } | null
  /** Evenement importe que son fournisseur a annule : il reste au calendrier, marque comme tel. */
  canceledAt: string | null
  /** `null` : evenement note a la main, ou importe sans que ses convocations soient connues. */
  participation: EventParticipation | null
  /** Rappel de l'utilisateur sur cet evenement, en minutes avant son debut ; `null` : il n'en a pas. */
  reminder: number | null
}

export interface SaveEvent {
  type: string
  title: string
  startsAt: string
  endsAt: string
  isAllDay: boolean
  location: string
  description: string
  details: Record<string, string>
  /** A la creation : les autres personnes du calendrier a prevenir par notification. Absent, personne. */
  notify?: { everyone: boolean; userIds: string[] }
  /**
   * A la creation : le rappel pose avec l'evenement. `everyone` le pose aussi pour les autres personnes du
   * calendrier, qui reglent ou retirent ensuite le leur.
   */
  reminder?: { minutesBefore: number; everyone: boolean }
}

/** Une autre personne du calendrier : son proprietaire, ou le beneficiaire d'un partage. */
export interface CalendarMember {
  userId: string
  displayName: string
}

export type EventFieldKind = 'text' | 'number' | 'url' | 'boolean' | 'choice'

export interface EventField {
  key: string
  label: string
  kind: EventFieldKind
  required: boolean
  options: { value: string; label: string }[]
}

/** Type d'evenement : ses champs s'ajoutent aux proprietes de base dans le formulaire. */
export interface EventType {
  key: string
  label: string
  fields: EventField[]
}

export const roleLabels: Record<CalendarRole, string> = {
  owner: 'Proprietaire',
  fullControl: 'Controle total',
  readOnly: 'Lecture seule',
}

export const accessLabels: Record<CalendarAccess, string> = {
  readOnly: 'Lecture seule',
  fullControl: 'Controle total',
}

/**
 * Confort d'affichage : le serveur refait le controle a chaque ecriture. Un
 * calendrier lie reflete une autre application : ses evenements ne se
 * modifient pas ici, meme par son proprietaire.
 */
export function canEditEvents(calendar: CalendarSummary): boolean {
  return calendar.role !== 'readOnly' && calendar.source === standardSource
}

const calendarsKey = ['calendars'] as const
export const eventsKey = ['events'] as const
const sharesKey = (calendarId: string) => ['calendars', calendarId, 'shares'] as const
const membersKey = (calendarId: string) => ['calendars', calendarId, 'members'] as const

export function useCalendars() {
  return useQuery({ queryKey: calendarsKey, queryFn: () => apiGet<CalendarSummary[]>('/api/calendars') })
}

export function useCreateCalendar() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (calendar: { name: string; color: string; source: string }) =>
      apiSend<CalendarSummary>('/api/calendars', 'POST', calendar),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: calendarsKey }),
  })
}

export function useUpdateCalendar() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ calendarId, ...calendar }: { calendarId: string; name: string; color: string }) =>
      apiSend<null>(`/api/calendars/${calendarId}`, 'PUT', calendar),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: calendarsKey }),
  })
}

export function useDeleteCalendar() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (calendarId: string) => apiSend<null>(`/api/calendars/${calendarId}`, 'DELETE'),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: calendarsKey }),
        queryClient.invalidateQueries({ queryKey: eventsKey }),
      ]),
  })
}

export function useShares(calendarId: string) {
  return useQuery({
    queryKey: sharesKey(calendarId),
    queryFn: () => apiGet<CalendarShare[]>(`/api/calendars/${calendarId}/shares`),
  })
}

/**
 * Les autres personnes d'un calendrier ou l'utilisateur peut ecrire : celles
 * qu'il peut prevenir d'un nouvel evenement. Vide pour un calendrier non partage.
 */
export function useCalendarMembers(calendarId: string) {
  return useQuery({
    queryKey: membersKey(calendarId),
    enabled: calendarId !== '',
    queryFn: async () => {
      const members = await apiGet<CalendarMember[]>(`/api/calendars/${calendarId}/members`)
      return Array.isArray(members) ? members : []
    },
  })
}

export function useShareCalendar(calendarId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (share: { email: string; access: CalendarAccess }) =>
      apiSend<CalendarShare>(`/api/calendars/${calendarId}/shares`, 'PUT', share),
    // Les partages et les personnes du calendrier, qui en decoulent.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['calendars', calendarId] }),
  })
}

export function useUnshareCalendar(calendarId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userId: string) => apiSend<null>(`/api/calendars/${calendarId}/shares/${userId}`, 'DELETE'),
    // Les calendriers et les evenements aussi : un beneficiaire qui quitte un
    // partage perd les deux.
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: calendarsKey }),
        queryClient.invalidateQueries({ queryKey: eventsKey }),
      ]),
  })
}

export function useEventTypes() {
  return useQuery({
    queryKey: ['event-types'] as const,
    queryFn: () => apiGet<EventType[]>('/api/event-types'),
    // Le catalogue ne change qu'avec un deploiement.
    staleTime: Infinity,
  })
}

/** Evenements de tous les calendriers visibles, sur l'intervalle [from, to[. */
export function useEvents(from: string, to: string) {
  return useQuery({
    queryKey: [...eventsKey, from, to] as const,
    queryFn: () =>
      apiGet<CalendarEvent[]>(`/api/events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
    // La grille precedente reste affichee pendant le chargement du mois suivant.
    placeholderData: (previous) => previous,
  })
}

export function useCreateEvent() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ calendarId, event }: { calendarId: string; event: SaveEvent }) =>
      apiSend<CalendarEvent>(`/api/calendars/${calendarId}/events`, 'POST', event),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: eventsKey }),
  })
}

export function useUpdateEvent() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ eventId, event }: { eventId: string; event: SaveEvent }) =>
      apiSend<CalendarEvent>(`/api/events/${eventId}`, 'PUT', event),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: eventsKey }),
  })
}

export function useDeleteEvent() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (eventId: string) => apiSend<null>(`/api/events/${eventId}`, 'DELETE'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: eventsKey }),
  })
}

/** Messages par champ d'un `ValidationProblem`. */
export function fieldErrors(payload: unknown): Record<string, string> {
  if (typeof payload !== 'object' || payload === null) {
    return {}
  }

  const errors = (payload as { errors?: unknown }).errors
  if (typeof errors !== 'object' || errors === null) {
    return {}
  }

  return Object.fromEntries(
    Object.entries(errors as Record<string, unknown>)
      .map(([field, messages]) => [field, Array.isArray(messages) ? messages.join(' ') : ''] as const)
      .filter(([, message]) => message.length > 0),
  )
}
